import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAccount as useEVMAccount } from "wagmi";
import { useAccount as useSolanaAccount } from "../../lib/solana/useAccount";
import { ensureLocalWallet, type GlideWallet } from "../../lib/glideWallet";
import {
  ACTIVE_PROVIDER,
  createConnectedWalletProvider,
  createLocalWalletProvider,
  type DepositOwnerProvider,
} from "../../lib/depositOwner";
import memStore from "../../lib/memStore";
import * as Sentry from "@sentry/react";

// Connected-wallet addresses come from wagmi/Solana connectors and populate
// asynchronously, so awaitDepositOwnerAddress polls briefly before giving up.
const CONNECTED_WALLET_READY_TIMEOUT_MS = 3000;
const CONNECTED_WALLET_POLL_INTERVAL_MS = 100;

type GlideWalletContextType = {
  /** True once the active provider has populated its wallet addresses. */
  isReady: boolean;
  /**
   * Resolves the deposit owner address for a CAIP-2 chain ID, waiting for the
   * embedded wallet to finish initializing first. Use this at session creation
   * so the address is never dropped by racing an unfinished wallet init.
   */
  awaitDepositOwnerAddress: (chainId: string) => Promise<string | undefined>;
};

const GlideWalletContext = createContext<GlideWalletContextType | undefined>(
  undefined,
);

export function GlideWalletProvider({ children }: { children: ReactNode }) {
  const [localWallet, setLocalWallet] = useState<GlideWallet | null>(null);
  const [localReady, setLocalReady] = useState(ACTIVE_PROVIDER !== "local");

  const { address: evmAddress } = useEVMAccount();
  const { address: solanaAddress } = useSolanaAccount();

  useEffect(() => {
    if (ACTIVE_PROVIDER !== "local") return;
    ensureLocalWallet()
      .then(setLocalWallet)
      .finally(() => setLocalReady(true));
  }, []);

  const provider = useMemo(() => {
    if (ACTIVE_PROVIDER === "connected-wallet") {
      const parentAddress =
        memStore.getItem("connectedWalletAddress") ?? undefined;
      const parentChainId = memStore.getItem("currentChainId") ?? undefined;
      const isParentSolana = parentChainId?.startsWith("solana:");

      return createConnectedWalletProvider({
        evm: evmAddress ?? (!isParentSolana ? parentAddress : undefined),
        solana: solanaAddress ?? (isParentSolana ? parentAddress : undefined),
      });
    }
    return createLocalWalletProvider(localWallet);
  }, [evmAddress, solanaAddress, localWallet]);

  const isReady = useMemo(() => {
    if (ACTIVE_PROVIDER === "connected-wallet")
      return !!(evmAddress || solanaAddress);
    return localReady;
  }, [evmAddress, solanaAddress, localReady]);

  // Keep the latest provider in a ref so awaitDepositOwnerAddress (stable
  // identity) can read current addresses without recreating on every render.
  const providerRef = useRef<DepositOwnerProvider>(provider);
  useEffect(() => {
    providerRef.current = provider;
  }, [provider]);

  const awaitDepositOwnerAddress = useCallback(
    async (chainId: string): Promise<string | undefined> => {
      if (ACTIVE_PROVIDER === "local") {
        const wallet = await ensureLocalWallet();
        return createLocalWalletProvider(wallet).getDepositOwnerAddress(
          chainId,
        );
      }
      // The connected-wallet provider only serves EVM and Solana; skip polling
      // for chains it can never resolve so we neither burn the timeout nor emit
      // false-positive telemetry.
      const namespace = chainId.split(":")[0];
      if (namespace !== "eip155" && namespace !== "solana") return undefined;

      // providerRef.current is rebuilt as the connector addresses resolve, so
      // poll it until an address is available or the timeout elapses.
      const deadline = Date.now() + CONNECTED_WALLET_READY_TIMEOUT_MS;
      let address = providerRef.current.getDepositOwnerAddress(chainId);
      while (address === undefined && Date.now() < deadline) {
        await new Promise((resolve) =>
          setTimeout(resolve, CONNECTED_WALLET_POLL_INTERVAL_MS),
        );
        address = providerRef.current.getDepositOwnerAddress(chainId);
      }
      if (address === undefined) {
        Sentry.captureMessage(
          "[GlideWallet] connected-wallet address unresolved after timeout",
          { level: "warning", tags: { chainId } },
        );
      }
      return address;
    },
    [],
  );

  return (
    <GlideWalletContext.Provider value={{ isReady, awaitDepositOwnerAddress }}>
      {children}
    </GlideWalletContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGlideWallet(): GlideWalletContextType {
  const ctx = useContext(GlideWalletContext);
  if (!ctx) {
    throw new Error("useGlideWallet must be used within GlideWalletProvider");
  }
  return ctx;
}
